import React, { ComponentType, lazy, Suspense } from "react";
import { AuthRouteFallback } from "../components/common/Loaders/AuthRouteFallback";
import { PageLoader } from "../components/common/Loaders/PageLoader";

export type LazyPageOptions = {
  fallback?: React.ReactNode;
};

/**
 * Wraps a dynamic import in Suspense with the app-standard page loader.
 */
export function lazyPage<PageProps extends object>(
  factory: () => Promise<{ default: ComponentType<PageProps> }>,
  props?: PageProps & { key?: React.Key },
  options?: LazyPageOptions
): React.ReactElement {
  const LazyComponent = lazy(factory);
  const { key: reactKey, ...componentProps } =
    props ?? ({} as PageProps & { key?: React.Key });

  return (
    <Suspense fallback={options?.fallback ?? <PageLoader />}>
      <LazyComponent key={reactKey} {...(componentProps as PageProps)} />
    </Suspense>
  );
}

/** Lazy route under AuthLayout — uses an in-flow loader so the form area keeps height. */
export function lazyAuthPage<PageProps extends object>(
  factory: () => Promise<{ default: ComponentType<PageProps> }>,
  props?: PageProps & { key?: React.Key }
): React.ReactElement {
  return lazyPage(factory, props, { fallback: <AuthRouteFallback /> });
}
